"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { forwardRef, useMemo } from "react";
import type { PrizeWheelSegmentProps } from "./types";

const tierGradients = {
  yellow: "linear-gradient(0deg, #FF8D02 60%, #FFB516 100%)", // Legendary
  grey: "linear-gradient(0deg, rgba(109, 108, 109, 0.60) 60%, #F3F3F3 100%)", // Common
  green: "linear-gradient(0deg, #007818 50%, #3CD25B 100%)", // Uncommon
  blue: "linear-gradient(0deg, #0076D5 60%, #46ACFF 100%)", // Rare
  purple: "linear-gradient(0deg, rgba(156, 45, 255, 0.60) 60%, #9C2DFF 100%)", // Epic
};

const PrizeWheelSegment = forwardRef<
  HTMLDivElement,
  PrizeWheelSegmentProps & {
    segmentAngle: number;
    wheelSize: number;
  }
>(
  (
    {
      data,
      size = "md",
      rotation = 0,
      segmentAngle,
      wheelSize,
      className,
      onClick,
      ...props
    },
    ref,
  ) => {
    const handleClick = () => {
      onClick?.(data);
    };

    const radius = wheelSize / 2;
    const centerX = radius;
    const centerY = radius;

    // Calculate the arc endpoints for this segment
    const startAngleRad = useMemo(() => -Math.PI / 2, []); // Start at top (12 o'clock)
    const endAngleRad = useMemo(
      () => startAngleRad + (segmentAngle * Math.PI) / 180,
      [startAngleRad, segmentAngle],
    );

    const x1 = centerX + radius * Math.cos(startAngleRad);
    const y1 = centerY + radius * Math.sin(startAngleRad);
    const x2 = centerX + radius * Math.cos(endAngleRad);
    const y2 = centerY + radius * Math.sin(endAngleRad);

    // Use large arc flag if angle > 180 degrees
    const largeArcFlag = segmentAngle > 180 ? 1 : 0;

    // SVG path for the pie slice
    const pathData = useMemo(
      () =>
        `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`,
      [centerX, centerY, x1, y1, x2, y2, radius, largeArcFlag],
    );

    return (
      <div
        ref={ref}
        className={cn(
          "absolute select-none cursor-pointer transition-transform duration-200 hover:scale-105",
          className,
        )}
        style={{
          width: wheelSize,
          height: wheelSize,
          transform: `rotate(${rotation}deg)`,
          transformOrigin: "center center",
        }}
        onClick={handleClick}
        aria-label={`Prize segment: ${data.label}`}
        {...props}
      >
        <div
          className="absolute inset-0"
          style={useMemo(
            () => ({
              background: tierGradients[data.tier],
              maskImage: `url("data:image/svg+xml,${encodeURIComponent(`<svg viewBox='0 0 ${wheelSize} ${wheelSize}' xmlns='http://www.w3.org/2000/svg'><path d='${pathData}' fill='white'/></svg>`)}")`,
              maskSize: `${wheelSize}px ${wheelSize}px`,
              maskRepeat: "no-repeat",
              maskPosition: "0px 0px",
            }),
            [data.tier, wheelSize, pathData],
          )}
        />
        <svg
          width={wheelSize}
          height={wheelSize}
          className="absolute inset-0 pointer-events-none"
          aria-hidden="true"
          focusable="false"
        >
          <path d={pathData} fill="transparent" stroke="none" />
        </svg>

        {/* Prize image positioned near outer edge using polar coordinates */}
        {data.imageUrl &&
          (() => {
            // Calculate position responsively
            const imageRadius = radius * 0.9;
            const imageAngle = segmentAngle / 2; // Middle of segment
            const isCompactImg = wheelSize < 520;
            const circleRatio = isCompactImg ? 0.08 : 0.09; // background circle diameter ratio (mobile bumped)
            const imageAngleRad = (imageAngle * Math.PI) / 180;
            const imageX = centerX + imageRadius * Math.sin(imageAngleRad);
            const imageY = centerY - imageRadius * Math.cos(imageAngleRad);

            return (
              <div
                className="absolute"
                style={{
                  left: imageX - wheelSize * (circleRatio / 2),
                  top: imageY - wheelSize * (circleRatio / 2),
                  width: wheelSize * circleRatio,
                  height: wheelSize * circleRatio,
                }}
              >
                <div
                  className="relative rounded-full"
                  style={{
                    transform: `rotate(${imageAngle}deg)`,
                    transformOrigin: "center",
                    width: "100%",
                    height: "100%",
                    backgroundImage:
                      "url('/assets/prize-wheel/prize-background.webp')",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <Image
                    src={data.imageUrl}
                    alt={`${data.label} prize`}
                    fill
                    className="object-contain"
                    sizes="(max-width: 768px) 20vw, 84px"
                    quality={95}
                    style={{
                      // Keep icon absolute size roughly constant while circle grows on mobile
                      // by scaling inversely with the circleRatio change.
                      transform: (() => {
                        const baseScale = wheelSize < 520 ? 1.42 : 1.26;
                        const baseCircle = wheelSize < 520 ? 0.07 : 0.09;
                        const adjusted = baseScale * (baseCircle / circleRatio);
                        const offsetY = Math.max(0.5, wheelSize * 0.01); // nudge upward a fraction
                        return `translateY(-${offsetY}px) scale(${adjusted})`;
                      })(),
                      transformOrigin: "center",
                      willChange: "transform",
                    }}
                  />
                </div>
              </div>
            );
          })()}

        {(() => {
          // Anchor text at the outer side of the segment, 5px in from the image badge
          const imageContainerPx = (wheelSize < 520 ? 0.07 : 0.09) * wheelSize; // Match image diameter above
          const desiredGapPx = 5;
          const imageRadiusPx = radius * 0.9;
          const textRadius = Math.max(
            imageRadiusPx - (imageContainerPx / 2 + desiredGapPx),
            radius * 0.55,
          );
          const textAngle = segmentAngle / 2; // Middle of segment
          const textAngleRad = (textAngle * Math.PI) / 180;
          const textX = centerX + textRadius * Math.sin(textAngleRad);
          const textY = centerY - textRadius * Math.cos(textAngleRad);

          // Calculate rotation so right side of text faces outward
          // Text should be tangent to the arc with right side pointing outward
          const textRotation = textAngle - 90; // Subtract 90° to make text tangent to arc

          const fontSize = (() => {
            if (wheelSize < 400) return 8; // Very small wheels
            if (wheelSize < 520) return 10; // Small wheels
            if (wheelSize < 600) return 12; // Medium wheels
            return 14; // Large wheels (14pt for desktop)
          })();
          const strokeWidth = (() => {
            if (fontSize < 10) return 0.6;
            if (fontSize < 12) return 0.8;
            if (fontSize < 14) return 1.0;
            return 1.2;
          })();

          const { px, py } = (() => {
            if (fontSize < 10) return { px: "5px", py: "4px" };
            if (fontSize < 12) return { px: "7px", py: "6px" };
            if (fontSize < 14) return { px: "9px", py: "8px" };
            return { px: "9px", py: "8px" };
          })();

          const textShadow = (() => {
            const o = strokeWidth;
            return `0 ${o}px 0 #000, 0 -${o}px 0 #000, ${o}px 0 0 #000, -${o}px 0 0 #000, ${o}px ${o}px 0 #000, -${o}px ${o}px 0 #000, ${o}px -${o}px 0 #000, -${o}px -${o}px 0 #000`;
          })();

          return (
            <div
              className="absolute flex items-center justify-start"
              style={{
                left: textX,
                top: textY,
                transform: `translate(-100%, -50%) rotate(${textRotation}deg)`,
                transformOrigin: "right center",
              }}
            >
              <span
                className="bg-black/50 text-white font-extrabold uppercase tracking-normal whitespace-nowrap rounded-full"
                style={{
                  textShadow,
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "geometricPrecision",
                  fontSize,
                  lineHeight: 1,
                  paddingTop: py,
                  paddingBottom: py,
                  paddingLeft: px,
                  paddingRight: px,
                }}
              >
                {data.label}
              </span>
            </div>
          );
        })()}
      </div>
    );
  },
);

export { PrizeWheelSegment };
export type { PrizeWheelSegmentProps };
